/**
 * Per-organisation finance. Two components live here.
 *
 * `TenantDetail` is the presentational screen: it renders data it is handed
 * as props and has no query of its own, so it is testable without a router.
 * `TenantDetailScreen` is the thin wrapper `routes.ts` mounts: it reads
 * `tenantId` off the URL, fetches, and hands what it got to `TenantDetail`.
 *
 * `spend` is `null` until the spend fetch has answered, and stays `null` when
 * it failed: the 30-day totals then render `Absent`, never a sum over nothing.
 * An empty array is a measured "no spend", which sums to a real zero.
 *
 * The credit balance is shown in organisation credits only; mixing provider
 * cost into it (platform design §18.1) is what makes a margin misread. There
 * is no dormancy panel and no purge dialog: platform design §18.3 removed
 * both, and nothing here describes an organisation as removable.
 *
 * `tracked_accounts`, `tenant_credits_per_day` and `funded_until` are `null`
 * under RLS containment for a platform administrator (platform design §18.4),
 * so all three render the fenced-value `Absent` rather than `Credits`' own
 * "sem preço" -- that copy is for an operation genuinely priced at nothing,
 * never for a figure this side of the fence cannot see.
 *
 * `campaign_slots` is null when the organisation has no capacity row at all,
 * distinct from the RLS fence above: it renders `Absent` too, but for a
 * different reason, so it carries its own hint text rather than reusing
 * `detail.fenced.hint`.
 *
 * The slot card shows a used-of-total fraction, not a bare count:
 * `campaigns_in_use` is never null (it counts real campaigns whether or not
 * the tenant has a capacity row), so only `campaign_slots` being null
 * suppresses the fraction. The same fraction repeats in the Capacidade panel
 * below, this time with a meter bar and its own adjustment trigger --
 * `AdjustCapacity` mounts from the same two figures either trigger opens it
 * with, plus the balance the Capacidade panel's Créditos row shows.
 *
 * Movimentos' badge and `SpendChart`'s own subtitle both name the window in
 * whole days through `windowDays` below, computed from `last30Days()` -- the
 * same range `TenantDetailScreen` fetches the spend band under -- so neither
 * can name a span the query did not actually use.
 */

import { useState, type ReactNode } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { Plus } from 'lucide-react';

import { defineCatalogue, getLocale, t } from '../../i18n';
import { integer } from '../../i18n/format';
import { Absent } from '../../chrome/measure';
import { MeterBar } from '../../chrome/MeterBar';
import { Page, PageHeader, StatCard, StatGrid, PageSection } from '../../chrome/page';
import { useBreadcrumbLeaf } from '../../chrome/shell';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { useOpsMovements, useOpsTenant, useTenantSpend } from '../api/queries';
import type { OpsMovement, OpsTenantDetail, OperatorSpendRow } from '../api/schemas';
import { formatCalendarDay } from '../lib/date';
import { Credits, Margin, reachOf, ReachFigure } from '../lib/units';
import { GrantDialog } from '../credits/dialogs';
import { AdjustCapacity } from './AdjustCapacity';
import { MovementLedger } from './MovementLedger';
import { SpendChart } from './SpendChart';

const catalogue = defineCatalogue(
  {
    'detail.fenced.hint':
      'Administradores não podem ver dentro de uma organização, então isto permanece em branco.',
    'error.tenant': 'Não foi possível carregar esta organização.',
    'ledger.title': 'Movimentos',
    'ledger.error': 'Não foi possível carregar as movimentações.',
    'balance.label': 'Saldo',
    'slots.label': 'Vagas de campanha',
    'slots.fraction': '{used} de {total}',
    'slots.usage': 'em uso, contratadas',
    'slots.absentHint':
      'Esta organização ainda não possui alocação de vagas, então isto permanece em branco.',
    'held.label': 'Retido',
    'available.label': 'Disponível',
    'trackedAccounts.label': 'Contas rastreadas',
    'burnRate.label': 'Créditos por dia',
    'fundedUntil.label': 'Financiado até',
    'reach.label': 'Alcance',
    'prices.link': 'Editar preços',
    'grant.button': 'Conceder créditos',
    'capacity.title': 'Capacidade',
    'capacity.header.action': 'Ajustar capacidade',
    'capacity.row.action': 'Ajustar',
    'capacity.credits.caption': 'Saldo atual da organização.',
    'credits.title': 'Créditos',
    'spend30d.label': 'Gasto 30d',
    'margin30d.label': 'Margem 30d',
    'window.badge.other': 'últimos {n} dias',
    'window.badge.one': 'último dia',
  },
  {
    'detail.fenced.hint': 'Administrators cannot see inside an organisation, so this stays blank.',
    'error.tenant': 'Could not load this organisation.',
    'ledger.title': 'Movements',
    'ledger.error': 'Could not load the movements.',
    'balance.label': 'Balance',
    'slots.label': 'Campaign slots',
    'slots.fraction': '{used} of {total}',
    'slots.usage': 'in use, allocated',
    'slots.absentHint':
      'This organisation has no campaign-slot allocation yet, so this stays blank.',
    'held.label': 'Held',
    'available.label': 'Available',
    'trackedAccounts.label': 'Tracked accounts',
    'burnRate.label': 'Credits per day',
    'fundedUntil.label': 'Funded until',
    'reach.label': 'Reach',
    'prices.link': 'Edit prices',
    'grant.button': 'Grant credits',
    'capacity.title': 'Capacity',
    'capacity.header.action': 'Adjust capacity',
    'capacity.row.action': 'Adjust',
    'capacity.credits.caption': "The organisation's current balance.",
    'credits.title': 'Credits',
    'spend30d.label': 'Spend 30d',
    'margin30d.label': 'Margin 30d',
    'window.badge.other': 'last {n} days',
    'window.badge.one': 'last day',
  },
);

/** The Movimentos and Gastos diários headers both name the span they cover
 *  in whole days -- derived from the range itself, never a literal "30",
 *  so a changed window can't leave the badge naming the old one. Exported so
 *  a test can mutate the range independently of the clock `last30Days`
 *  reads. */
export function windowDays(range: { from: string; to: string }): number {
  const ms = new Date(range.to).getTime() - new Date(range.from).getTime();
  return Math.max(1, Math.round(ms / 86_400_000));
}

function WindowBadge(props: { days: number }): ReactNode {
  return (
    <Badge variant="secondary" data-testid="window-badge">
      {catalogue.plural('window.badge.other', props.days, {
        n: integer(props.days, getLocale()),
      })}
    </Badge>
  );
}

type CapacityDialog = 'slots' | 'credits';

export function TenantDetail(props: {
  tenant: OpsTenantDetail;
  movements: OpsMovement[];
  movementsLoading?: boolean;
  movementsError?: boolean;
  spend: OperatorSpendRow[] | null;
  spendError?: boolean;
}): ReactNode {
  const { tenant, spend } = props;
  const [openDialog, setOpenDialog] = useState<'grant' | CapacityDialog | null>(null);
  const reach = reachOf(tenant.available_credits, tenant.tenant_credits_per_day);
  useBreadcrumbLeaf(tenant.name);
  const totals =
    spend === null
      ? null
      : {
          tenantCredits: spend.reduce((total, row) => total + row.tenant_credits, 0),
          providerCredits: spend.reduce((total, row) => total + row.provider_credits, 0),
        };
  const fenced = <Absent reason={{ text: catalogue.t('detail.fenced.hint') }} />;
  const days = windowDays(last30Days());
  const capacityOpen = openDialog === 'slots' || openDialog === 'credits';

  return (
    <Page>
      <PageHeader
        title={tenant.name}
        actions={
          <Button onClick={() => setOpenDialog('slots')}>
            <Plus aria-hidden />
            {catalogue.t('capacity.header.action')}
          </Button>
        }
      />

      <StatGrid>
        <StatCard
          label={catalogue.t('balance.label')}
          value={<Credits value={tenant.balance_credits} unit="tenant" />}
        />
        <StatCard
          label={catalogue.t('slots.label')}
          value={
            <span data-testid="campaign-slots">
              {tenant.campaign_slots === null ? (
                <Absent reason={{ text: catalogue.t('slots.absentHint') }} />
              ) : (
                catalogue.t('slots.fraction', {
                  used: integer(tenant.campaigns_in_use, getLocale()),
                  total: integer(tenant.campaign_slots, getLocale()),
                })
              )}
            </span>
          }
          caption={tenant.campaign_slots === null ? undefined : catalogue.t('slots.usage')}
        />
        <StatCard
          label={catalogue.t('trackedAccounts.label')}
          value={
            <span data-testid="tracked-accounts">
              {tenant.tracked_accounts === null
                ? fenced
                : integer(tenant.tracked_accounts, getLocale())}
            </span>
          }
        />
        <StatCard
          label={catalogue.t('spend30d.label')}
          value={
            <span data-testid="spend-30d">
              {totals === null ? (
                <Absent reason="not_measured" />
              ) : (
                <Credits value={totals.tenantCredits} unit="tenant" />
              )}
            </span>
          }
        />
        <StatCard
          label={catalogue.t('margin30d.label')}
          value={
            <span data-testid="margin-30d">
              {totals === null ? (
                <Absent reason="not_measured" />
              ) : (
                <Margin
                  tenantCredits={totals.tenantCredits}
                  providerCredits={totals.providerCredits}
                />
              )}
            </span>
          }
        />
        <StatCard
          label={catalogue.t('available.label')}
          value={<Credits value={tenant.available_credits} unit="tenant" />}
        />
        <StatCard
          label={catalogue.t('held.label')}
          value={<Credits value={tenant.held_credits} unit="tenant" />}
        />
        <StatCard
          label={catalogue.t('reach.label')}
          value={
            <div className="flex flex-col items-start gap-1">
              <ReachFigure reach={reach} />
              <Link
                to="/operations"
                className="text-caption text-primary-ink underline underline-offset-4"
              >
                {catalogue.t('prices.link')}
              </Link>
            </div>
          }
        />
        <StatCard
          label={catalogue.t('burnRate.label')}
          value={
            <span data-testid="burn-rate">
              {tenant.tenant_credits_per_day === null ? (
                fenced
              ) : (
                <Credits value={tenant.tenant_credits_per_day} unit="tenant" />
              )}
            </span>
          }
        />
        <StatCard
          label={catalogue.t('fundedUntil.label')}
          value={
            <span data-testid="funded-until">
              {tenant.funded_until === null
                ? fenced
                : formatCalendarDay(tenant.funded_until, getLocale())}
            </span>
          }
        />
      </StatGrid>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <PageSection
            title={catalogue.t('ledger.title')}
            actions={<WindowBadge days={days} />}
            card={false}
          >
            {props.movementsError ? (
              <p role="alert" className="text-body-sm">
                {catalogue.t('ledger.error')}
              </p>
            ) : (
              <MovementLedger movements={props.movements} loading={props.movementsLoading} />
            )}
          </PageSection>
        </div>

        <PageSection title={catalogue.t('capacity.title')}>
          <div className="flex flex-col gap-2">
            <div className="flex items-start justify-between gap-2">
              <p className="text-label text-muted-foreground">{catalogue.t('slots.label')}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setOpenDialog('slots')}
              >
                {catalogue.t('capacity.row.action')}
              </Button>
            </div>
            <p className="text-title-md" data-testid="capacity-slots">
              {tenant.campaign_slots === null ? (
                <Absent reason={{ text: catalogue.t('slots.absentHint') }} />
              ) : (
                catalogue.t('slots.fraction', {
                  used: integer(tenant.campaigns_in_use, getLocale()),
                  total: integer(tenant.campaign_slots, getLocale()),
                })
              )}
            </p>
            <MeterBar
              value={tenant.campaign_slots === null ? null : tenant.campaigns_in_use}
              max={tenant.campaign_slots ?? 0}
              label={catalogue.t('slots.label')}
            />
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-start justify-between gap-2">
              <p className="text-label text-muted-foreground">{catalogue.t('credits.title')}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setOpenDialog('credits')}
              >
                {catalogue.t('capacity.row.action')}
              </Button>
            </div>
            <p className="text-title-md" data-testid="capacity-balance">
              <Credits value={tenant.balance_credits} unit="tenant" />
            </p>
            <p className="text-caption text-muted-foreground">
              {catalogue.t('capacity.credits.caption')}
            </p>
          </div>
          <Button variant="ghost" onClick={() => setOpenDialog('grant')}>
            {catalogue.t('grant.button')}
          </Button>
        </PageSection>
      </div>

      <SpendChart data={spend} error={props.spendError} days={days} />

      <GrantDialog
        tenantId={tenant.tenant_id}
        tenantName={tenant.name}
        availableCredits={tenant.available_credits}
        tenantCreditsPerDay={tenant.tenant_credits_per_day}
        open={openDialog === 'grant'}
        onOpenChange={(open) => setOpenDialog(open ? 'grant' : null)}
      />
      <AdjustCapacity
        key={openDialog === 'credits' ? 'credits' : 'slots'}
        tenantId={tenant.tenant_id}
        tenantName={tenant.name}
        campaignSlots={tenant.campaign_slots}
        campaignsInUse={tenant.campaigns_in_use}
        initialTab={openDialog === 'credits' ? 'credits' : 'slots'}
        open={capacityOpen}
        onOpenChange={(open) => {
          if (!open) setOpenDialog(null);
        }}
      />
    </Page>
  );
}

/** ASSUMED window: no range is specified for the finance band; 30 days is a
 *  placeholder pending a specified one. Exported so a story can compute the
 *  same `opsKeys.tenantUsage` key `TenantDetailScreen` fetches under, and so
 *  `windowDays` above and the two window badges name exactly the range this
 *  screen actually fetches spend for. */
export function last30Days(): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - 30 * 86_400_000);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

export function TenantDetailScreen(): ReactNode {
  const { tenantId } = useParams({ strict: false }) as { tenantId: string };
  const tenantQuery = useOpsTenant(tenantId);
  const movementsQuery = useOpsMovements(tenantId);
  const spendQuery = useTenantSpend(tenantId, last30Days());

  if (tenantQuery.isPending) {
    return (
      <p role="status" className="text-body-sm text-muted-foreground py-8">
        {t('shell.loading')}
      </p>
    );
  }

  if (tenantQuery.data === undefined) {
    return (
      <div role="alert" className="text-body-sm space-y-3 py-8">
        <p>{catalogue.t('error.tenant')}</p>
        <Button variant="outline" onClick={() => void tenantQuery.refetch()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  return (
    <TenantDetail
      tenant={tenantQuery.data}
      movements={movementsQuery.data ?? []}
      movementsLoading={movementsQuery.isPending}
      movementsError={movementsQuery.isError}
      spend={spendQuery.isSuccess ? spendQuery.data : null}
      spendError={spendQuery.isError}
    />
  );
}
